/**
 * @fileoverview Shared template-editor state contracts and serialization helpers.
 * @module import-editor/templateEditor-types
 *
 * Responsibilities:
 * - Define the in-memory authoring state used by the shared import-template wizard
 * - Translate between editor state and persisted import-template documents
 * - Preserve draft resume metadata required for local-first reopen flows
 *
 * Dependencies:
 * - `@firesquirrel/local-db` import template contracts
 *
 * Architecture:
 * - Authoring state is local-first and serializes into Dexie-backed template documents
 * - Draft resume metadata preserves entry gate, completed steps, and current tab
 * - Published templates retain lineage metadata while clearing draft-only resume payloads
 *
 * Notes:
 * - Draft saves must remain valid before the user has provided a final template name
 */

import type {
  ImportTemplate,
  ImportBehavior,
  ImportDataMode,
  FieldMapping,
  ImportTemplateSampleBlob,
  ImportTemplateSamplePreview,
  ImportTemplateMappingSuggestion,
  ImportTemplateVisibility,
  ImportTemplateEntryGate,
  VendorHint,
} from '@firesquirrel/local-db';

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

/**
 * The canonical built-in tab ids. Host apps may inject additional tab ids
 * (e.g. system-web's 'visibility') via `useTemplateEditorState`'s `tabs`
 * option. State fields that store a tab id therefore use `string` so both
 * built-in and host-supplied ids flow through the same machinery without
 * a type assertion at every boundary.
 */
export type TemplateEditorTab = 'basics' | 'sampleData' | 'mappings' | 'matchKey' | 'geocoding' | 'review';

/**
 * Describes a tab the editor machinery needs to know about — built-in or
 * host-supplied. Kept minimal so host apps can render arbitrary tab
 * content via the `UnifiedImportWizard`'s render slots.
 */
export interface EditorTabDescriptor {
  id: string;
  label: string;
}

// ---------------------------------------------------------------------------
// Template authoring mode
// ---------------------------------------------------------------------------
// A template is authored in one of two mutually-exclusive modes:
//
//   'bound' — the author has (or will upload) a sample file, and is mapping
//              concrete source columns to target fields. Produces either a
//              standalone bound template or a variant of a shell.
//
//   'shell' — the author is defining a reusable blueprint for a recurring
//              import whose source columns vary by file. Mappings are deferred
//              to import time; the editor hides the Field Mappings step.
//
// `null` means the user has not chosen yet; the editor renders the gate card
// instead of the tab layout.
export type TemplateEditorMode = 'bound' | 'shell' | null;

export const DEFAULT_DRAFT_TEMPLATE_NAME = 'Untitled import template draft';

export const EDITOR_TABS: { id: TemplateEditorTab; label: string }[] = [
  { id: 'basics',     label: 'Basics' },
  // Upload a sample file here; the parser returns columns + up to 100 sample
  // rows which become the source of truth for the Field Mappings tab
  // (dropdown options, ? preview, AI suggest). Hidden in shell mode since
  // shell-mode templates defer the file to import time.
  { id: 'sampleData', label: 'Sample Data' },
  { id: 'mappings',   label: 'Field Mappings' },
  { id: 'matchKey',   label: 'Match & Dedup' },
  { id: 'geocoding',  label: 'Geocoding' },
  { id: 'review',     label: 'Review + Publish' },
];

export const TAB_LABELS = EDITOR_TABS.map(t => t.label);

export function getTabIndex(tab: TemplateEditorTab): number {
  return EDITOR_TABS.findIndex(t => t.id === tab);
}

export function getTabByIndex(index: number): TemplateEditorTab {
  const safe = Math.min(Math.max(index, 0), EDITOR_TABS.length - 1);
  return EDITOR_TABS[safe]!.id;
}

/**
 * Returns whether the geocoding tab applies to the given dataMode. Item 2
 * (2026-05-08): geocoding only makes sense for `contact` and `generic`
 * authoring — rows in those modes typically carry an address but no
 * coordinates, so the wizard offers an opt-in geocoding pass. `parcel`
 * imports already supply a polygon + centroid (lat/lng come from the
 * boundary, not from a separate address), and `lookup` rows are not
 * spatial at all. Hiding the tab for those modes prevents authors from
 * (accidentally) configuring an address-to-point pipeline that would
 * never run.
 *
 * Returns `true` when dataMode is undefined so callers in legacy code
 * paths that don't yet thread dataMode behave the same as before this
 * change (i.e. the tab stays visible).
 */
export function isGeocodingApplicable(dataMode: ImportDataMode | null | undefined): boolean {
  if (dataMode === undefined || dataMode === null) return true;
  return dataMode !== 'parcel' && dataMode !== 'lookup';
}

// Tabs visible for a given authoring mode. Shell mode hides the Field Mappings
// tab because mappings do not exist until import time. Consumers pass the
// filtered list into <EditorTabBar /> and use it to filter goNext/goBack.
//
// Item 2 (2026-05-08): the optional `dataMode` argument lets callers strip
// the Geocoding tab for parcel/lookup imports. When omitted, behavior is
// unchanged (the geocoding tab is included) so existing callers keep
// working without modification.
export function getVisibleEditorTabs(
  mode: TemplateEditorMode,
  dataMode?: ImportDataMode | null,
): { id: TemplateEditorTab; label: string }[] {
  let tabs: { id: TemplateEditorTab; label: string }[];
  if (mode === 'shell') {
    // Shell mode hides BOTH the sample data tab and the field mappings
    // tab: mappings do not exist until import time, and without mappings
    // to build there is nothing to do with a sample file either.
    tabs = EDITOR_TABS.filter(t => t.id !== 'mappings' && t.id !== 'sampleData');
  } else {
    tabs = [...EDITOR_TABS];
  }
  if (!isGeocodingApplicable(dataMode)) {
    tabs = tabs.filter(t => t.id !== 'geocoding');
  }
  return tabs;
}

export function isTabVisible(
  tab: TemplateEditorTab,
  mode: TemplateEditorMode,
  dataMode?: ImportDataMode | null,
): boolean {
  if (mode === 'shell' && (tab === 'mappings' || tab === 'sampleData')) return false;
  if (tab === 'geocoding' && !isGeocodingApplicable(dataMode)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Blade state (for FieldMappingDetailBlade)
// ---------------------------------------------------------------------------

export interface BladeState {
  open: boolean;
  editingIndex: number | null;
  draft: Partial<FieldMapping>;
}

export function emptyBladeState(): BladeState {
  return { open: false, editingIndex: null, draft: {} };
}

// ---------------------------------------------------------------------------
// Editor state
// ---------------------------------------------------------------------------

export interface TemplateEditorState {
  _id: string | null;
  isDraft: boolean;
  dirty: boolean;

  // UI
  //
  // activeTab / visitedTabs are typed as `string` (not the strict
  // TemplateEditorTab union) so host apps can inject additional tab ids
  // through `useTemplateEditorState`'s `tabs` option without forcing a
  // cast at every call site. Built-in tab logic compares against the
  // literal ids ('basics', 'mappings', ...) which narrows safely.
  activeTab: string;
  visitedTabs: Set<string>;
  completedTabs: Set<string>;
  bladeState: BladeState;

  // Tab 1 — Basics
  name: string;
  description: string;
  dataMode: ImportDataMode;
  importBehavior: ImportBehavior;
  expectedFileTypes: string[];
  vendorHint: VendorHint;

  // Tab 2 — Field Mappings
  fieldMappings: FieldMapping[];

  // Tab 3 — Match & Dedup
  matchKeyField: string;

  // Tab 4 — Geocoding
  geocodingEnabled: boolean;
  geocodingAddressFields: string[];
  boundaryCheckEnabled: boolean;

  // Tab 6 — Visibility (system-web only, stored here so stateToTemplate works universally)
  visibility: ImportTemplateVisibility;
  allowedTenantIds: string[];

  // Shell / variant relationship. Undefined/null means "standalone" (shell
  // if fieldMappings empty, bound otherwise). A non-null value means this
  // template is a variant derived from the referenced shell.
  parentTemplateId: string | null;

  // Authoring mode — bound (with mappings) vs shell (deferred). Null on a
  // brand-new template means the user has not chosen yet; the editor shows
  // the TemplateModeGate until this is set.
  mode: TemplateEditorMode;

  // scope
  scope: 'department' | 'system';

  // Authoring origin + resume metadata. Populated by the host app
  // (system-web or tenant-web) at editor open. `status` tracks whether
  // the current template is draft vs published so the wizard can wire
  // the correct Save Draft / Publish flow and downstream list views
  // can filter drafts out of run pickers without a second fetch.
  //
  // `creationFlow` records the wizard entrance — the run-flow's
  // save-as-variant and save-as-new-template paths hide certain tabs
  // (Visibility, Review) on reopen so the author returns to exactly
  // the set of steps they started with.
  origin: 'system-web' | 'tenant-web';
  originTenantId?: string;
  status: 'draft' | 'published' | 'archived';
  entryGate: ImportTemplateEntryGate | null;
  creationFlow: 'authoring' | 'run-variant' | 'run-new-template';
  runTenantId?: string;
  runTenantName?: string;
  samplePreview?: ImportTemplateSamplePreview;
  sampleBlob?: ImportTemplateSampleBlob;
  /** AI/rule-engine column match suggestions, persisted with the draft so the
      author sees the same detected matches after a hard refresh without waiting
      for another AI round-trip. Cleared on publish and reset on new file parse. */
  aiSuggestions?: ImportTemplateMappingSuggestion[];
  /** Source column names the author explicitly rejected (X button). Persisted
      with the draft so that re-running AI on the same template does not
      re-offer columns the author already decided aren't a match.
      Reset when the author uploads a new file. Cleared on publish. */
  aiDismissed?: string[];
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export interface EditorFactoryOptions {
  /**
   * Authoring app. Defaults to 'system-web' for scope='system' and
   * 'tenant-web' for scope='department'. The host may override when
   * the inference is wrong (e.g. a system admin drafting a department
   * template on behalf of a tenant).
   */
  origin?: 'system-web' | 'tenant-web';
  /** Required when origin='tenant-web' — the owning tenant id. */
  originTenantId?: string;
  /** First authoring gate chosen by the user. */
  entryGate?: ImportTemplateEntryGate;
  /**
   * Which wizard the user entered from. 'authoring' for the Templates
   * page → New Template flow; 'run-variant' / 'run-new-template' for
   * the Save cards fired after a run. Persisted into draftResume so
   * reopening a draft restores the correct gate.
   */
  creationFlow?: 'authoring' | 'run-variant' | 'run-new-template';
}

export function emptyEditorState(
  scope: 'department' | 'system' = 'department',
  options: EditorFactoryOptions = {},
): TemplateEditorState {
  const origin = options.origin
    ?? (scope === 'system' ? 'system-web' : 'tenant-web');
  const creationFlow = options.creationFlow ?? 'authoring';
  return {
    _id: null,
    isDraft: true,
    dirty: false,
    activeTab: 'basics',
    visitedTabs: new Set<string>(['basics']),
    completedTabs: new Set<string>(),
    bladeState: emptyBladeState(),
    name: '',
    description: '',
    dataMode: 'parcel',
    importBehavior: 'upsert',
    expectedFileTypes: [],
    vendorHint: 'unknown',
    fieldMappings: [],
    matchKeyField: '',
    geocodingEnabled: false,
    geocodingAddressFields: [],
    // Default ON for new templates — fire departments almost always want to
    // exclude parcels outside their district. Lookup-mode templates ignore
    // this flag so the default is safe regardless of dataMode.
    boundaryCheckEnabled: true,
    visibility: 'system-only',
    allowedTenantIds: [],
    parentTemplateId: null,
    // null → editor renders the TemplateModeGate to force a choice before
    // the user can see the tab-based editor UI.
    mode: null,
    scope,
    origin,
    originTenantId: options.originTenantId,
    status: 'draft',
    entryGate: options.entryGate ?? null,
    creationFlow,
    runTenantId: undefined,
    runTenantName: undefined,
    samplePreview: undefined,
    sampleBlob: undefined,
    aiSuggestions: undefined,
    aiDismissed: undefined,
  };
}

export function resolveDraftTemplateName(name: string): string {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : DEFAULT_DRAFT_TEMPLATE_NAME;
}

// ---------------------------------------------------------------------------
// Hydrate from existing template
// ---------------------------------------------------------------------------

export function templateToState(template: ImportTemplate): TemplateEditorState {
  const matchKeyMapping = template.fieldMappings?.find(m => m.isMatchKey);
  // Draft resume — when the template is a draft and carries a
  // draftResume blob, the editor restores the author's exact wizard
  // position: same active tab, same visited tabs, same authoring mode.
  // Published templates (status !== 'draft' and !isDraft) always open
  // on the Basics tab with `mode` re-derived from fieldMappings so the
  // Review-and-edit path stays predictable.
  const isDraft = template.isDraft === true || template.status === 'draft';
  const resume = isDraft ? template.draftResume : undefined;
  const fallbackMode: TemplateEditorMode =
    (template.fieldMappings?.length ?? 0) > 0 ? 'bound' : 'shell';
  // Default origin for legacy templates without a persisted origin:
  // system-scope templates were system-web authored, department-scope
  // templates were tenant-web authored. This keeps the round-trip safe
  // for documents written before the field existed.
  const origin = template.origin
    ?? (template.scope === 'system' ? 'system-web' : 'tenant-web');
  const fallbackEntryGate: ImportTemplateEntryGate = template.entryGate
    ?? resume?.entryGate
    ?? (resume?.runTenantId
      ? 'gate-3'
      : (fallbackMode === 'shell' ? 'gate-1' : 'gate-2'));
  return {
    _id: template._id,
    isDraft,
    dirty: false,
    activeTab: resume?.activeTab ?? 'basics',
    visitedTabs: new Set<string>(resume?.visitedTabs ?? ['basics']),
    completedTabs: new Set<string>(resume?.completedTabs ?? []),
    bladeState: emptyBladeState(),
    name: template.name,
    description: template.description ?? '',
    dataMode: template.dataMode,
    importBehavior: template.importBehavior,
    expectedFileTypes: template.expectedFileTypes ?? [],
    vendorHint: template.vendorHint ?? 'unknown',
    fieldMappings: template.fieldMappings ?? [],
    matchKeyField: template.matchKeyField ?? matchKeyMapping?.targetField ?? '',
    geocodingEnabled: template.geocodingEnabled ?? false,
    geocodingAddressFields: template.geocodingAddressFields ?? [],
    boundaryCheckEnabled: template.boundaryCheckEnabled ?? false,
    visibility: template.visibility,
    allowedTenantIds: template.allowedTenantIds ?? [],
    parentTemplateId: template.parentTemplateId ?? null,
    // Prefer the persisted draft mode when resuming so a shell-mode
    // author who left mid-flow doesn't suddenly get the Field Mappings
    // tab just because draftResume happens to have an empty mappings
    // array. Fall back to deriving from mappings for published rows.
    mode: resume?.mode ?? fallbackMode,
    scope: template.scope,
    origin,
    originTenantId: template.originTenantId,
    status: template.status ?? (isDraft ? 'draft' : 'published'),
    entryGate: fallbackEntryGate,
    creationFlow: resume?.creationFlow ?? 'authoring',
    runTenantId: resume?.runTenantId,
    runTenantName: resume?.runTenantName,
    samplePreview: isDraft ? template.samplePreview : undefined,
    sampleBlob: isDraft ? template.sampleBlob : undefined,
    aiSuggestions: isDraft ? template.aiSuggestions : undefined,
    aiDismissed: isDraft ? template.aiDismissed : undefined,
  };
}

// ---------------------------------------------------------------------------
// Serialize to API payload
// ---------------------------------------------------------------------------

/**
 * Serialisation mode:
 *   'draft'   — persist as isDraft/status='draft' with a draftResume blob
 *               so reopening lands on the same tab/mode. For system-web
 *               origin the visibility is forced to 'system-only' so
 *               half-built drafts never leak to tenants regardless of
 *               whatever the author picked on the Visibility tab.
 *   'publish' — persist as status='published' with isDraft=false and
 *               draftResume=null (cleared). Visibility is honoured as-is.
 */
export type StateToTemplateMode = 'draft' | 'publish';

export function stateToTemplate(
  state: TemplateEditorState,
  saveMode: StateToTemplateMode = 'draft',
): Omit<ImportTemplate, '_id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'> {
  const isDraftSave = saveMode === 'draft';
  // Force system-only on draft saves from system-web so drafts never
  // fan out to tenants while the author is mid-flow. The user's chosen
  // visibility is preserved in state and re-applied when they publish.
  const effectiveVisibility = isDraftSave && state.origin === 'system-web'
    ? 'system-only'
    : state.visibility;
  const draftResume = isDraftSave
    ? {
        activeTab: state.activeTab,
        visitedTabs: Array.from(state.visitedTabs),
        completedTabs: Array.from(state.completedTabs),
        mode: state.mode,
        entryGate: state.entryGate ?? undefined,
        creationFlow: state.creationFlow,
        runTenantId: state.runTenantId,
        runTenantName: state.runTenantName,
      }
    : null;
  return {
    name: isDraftSave ? resolveDraftTemplateName(state.name) : state.name,
    description: state.description || undefined,
    scope: state.scope,
    dataMode: state.dataMode,
    importBehavior: state.importBehavior,
    visibility: effectiveVisibility,
    allowedTenantIds: state.allowedTenantIds,
    expectedFileTypes: state.expectedFileTypes,
    fieldMappings: state.fieldMappings,
    matchKeyField: state.matchKeyField || undefined,
    vendorHint: state.vendorHint,
    // Item 2 (2026-05-08): geocoding is non-applicable for parcel and
    // lookup imports. Force-clear the flag + address-field list on
    // persistence so a stale draft from before a mode change cannot
    // produce an inert geocoding pipeline on the server. See
    // `isGeocodingApplicable` in this module for the canonical rule.
    geocodingEnabled: state.dataMode === 'parcel' || state.dataMode === 'lookup'
      ? false
      : state.geocodingEnabled,
    geocodingAddressFields: state.dataMode === 'parcel' || state.dataMode === 'lookup'
      ? []
      : state.geocodingAddressFields,
    boundaryCheckEnabled: state.boundaryCheckEnabled,
    // null → omit from the API payload so the DB stays clean; only
    // variants send a real parentTemplateId up.
    parentTemplateId: state.parentTemplateId ?? undefined,
    isDraft: isDraftSave,
    status: isDraftSave ? 'draft' : 'published',
    origin: state.origin,
    originTenantId: state.originTenantId,
    entryGate: state.entryGate ?? undefined,
    samplePreview: isDraftSave ? state.samplePreview : undefined,
    sampleBlob: isDraftSave ? state.sampleBlob : undefined,
    aiSuggestions: isDraftSave ? state.aiSuggestions : undefined,
    aiDismissed: isDraftSave ? state.aiDismissed : undefined,
    draftResume: draftResume ?? undefined,
  };
}
